'use client';

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import { getEntriesWithFilter } from '@/lib/contentstack-sdk';
import dynamic from 'next/dynamic';
import { IOrLeadGenFormCmsProps } from '@/components/organisms/or-lead-gen-form/or-lead-gen-form.types';
import {
  EModalVariants,
  IModalRefProps,
} from '@/components/molecules/ml-modal/ml-modal.types';
import { MlModal } from '@/components/molecules/ml-modal/ml-modal';

interface LeadGenModalContextType {
  openLeadGenModal: (href: string) => void;
}

// Dynamically load OrLeadGenForm to avoid circular import
const OrLeadGenForm = dynamic(
  () =>
    import('@/components/organisms/or-lead-gen-form/or-lead-gen-form').then(
      (mod) => mod.OrLeadGenForm,
    ),
  { ssr: false },
);

const LeadGenModalContext = createContext<LeadGenModalContextType | undefined>(
  undefined,
);

export const useLeadGenModal = () => {
  const context = useContext(LeadGenModalContext);
  if (!context)
    throw new Error('useLeadGenModal must be used within LeadGenModalProvider');
  return context;
};

export const LeadGenModalProvider = ({ children }: { children: ReactNode }) => {
  const [leadGenForm, setLeadGenForm] = useState<IOrLeadGenFormCmsProps | null>(
    null,
  );
  const [params, setParams] = useState<string | undefined>(undefined);
  const modalRef = useRef<IModalRefProps>(null);
  const openLeadGenModal = useCallback((href?: string) => {
    if (!href) return;
    if (!href.startsWith(':openLeadGen') && !href.startsWith(':calendly'))
      return;

    const queryString = href.split('?')[1];
    if (!queryString) return;

    const params = new URLSearchParams(queryString);
    const formId = params.get('form_id');
    const uid = params.get('form_uid');
    const contactPageType = params.get('contact_page_type_url');

    if (contactPageType) {
      setParams(contactPageType);
    }

    const filters: Record<string, string> = {};
    if (formId) filters.form_id = formId;
    if (uid) filters.uid = uid;

    if (Object.keys(filters).length > 0) {
      getEntriesWithFilter({
        contentTypeUid: 'lead_generation_form',
        filters,
      }).then((res) => {
        const typedRes = res as IOrLeadGenFormCmsProps[];
        if (typedRes.length > 0) {
          setLeadGenForm(typedRes[0]);
          modalRef.current?.handleOpen();
        }
      });
    }
  }, []);

  useEffect(() => {
    if (leadGenForm) {
      modalRef.current?.handleOpen();
    }
  }, [leadGenForm]);

  return (
    <LeadGenModalContext.Provider value={{ openLeadGenModal }}>
      {children}
      {leadGenForm && (
        <MlModal ref={modalRef} variant={EModalVariants.LEAD_GEN}>
          <OrLeadGenForm
            formId={leadGenForm.form_id}
            formName={leadGenForm.title}
            heading={leadGenForm.heading}
            description={leadGenForm.description}
            headingElement={leadGenForm.heading_element?.select}
            headingVariant={leadGenForm.heading_variant?.select}
            formType={leadGenForm.form_type}
            isInModal={leadGenForm.is_in_modal}
            topSpacing={leadGenForm.top_spacing?.spacing_value}
            bottomSpacing={leadGenForm.bottom_spacing?.spacing_value}
            contactPageTypeUrl={params}
          />
        </MlModal>
      )}
    </LeadGenModalContext.Provider>
  );
};
